import { TOP_1000_COINS, CoinData } from '../data/coinList';

const BASE_PRICES: { [key: string]: number } = {
  bitcoin: 97325.00,
  ethereum: 3456.78,
  tether: 1.00,
  binancecoin: 621.45,
  solana: 243.67,
  ripple: 2.34,
  'usd-coin': 1.00,
  'staked-ether': 3445.90,
  cardano: 0.98,
  dogecoin: 0.42,
  tron: 0.24,
  'the-open-network': 5.67,
  chainlink: 23.45,
  'avalanche-2': 45.67,
  'matic-network': 1.56,
  'wrapped-bitcoin': 97200.00,
  polkadot: 9.87,
  dai: 1.00,
  litecoin: 123.45,
  'shiba-inu': 0.00003456,
};

const MARKET_CAP_MULTIPLIERS: { [key: string]: number } = {
  bitcoin: 19500000,
  ethereum: 120000000,
  tether: 95000000000,
  binancecoin: 150000000,
  solana: 390000000,
  ripple: 52000000000,
  'usd-coin': 24000000000,
  'staked-ether': 9500000,
  cardano: 35000000000,
  dogecoin: 141000000000,
};

function generateRandomPrice(basePrice: number, volatility: number = 0.1): number {
  const change = (Math.random() - 0.5) * volatility;
  return basePrice * (1 + change);
}

function generatePercentageChange(range: number = 20): number {
  return (Math.random() - 0.5) * range;
}

function generateSparkline(basePrice: number, points: number = 168): number[] {
  const sparkline: number[] = [];
  let currentPrice = basePrice * (1 + (Math.random() - 0.5) * 0.1);
  
  for (let i = 0; i < points; i++) {
    const change = (Math.random() - 0.5) * 0.02;
    currentPrice = currentPrice * (1 + change);
    sparkline.push(currentPrice);
  }
  
  return sparkline;
}

function generateCoinData(coin: typeof TOP_1000_COINS[0], rank: number): CoinData {
  const basePrice = BASE_PRICES[coin.id] || Math.random() * 100 + 0.01;
  const currentPrice = generateRandomPrice(basePrice);
  const marketCapMultiplier = MARKET_CAP_MULTIPLIERS[coin.id] || Math.random() * 1000000000 + 1000000;
  
  const circulatingSupply = marketCapMultiplier;
  const marketCap = currentPrice * circulatingSupply;
  
  return {
    rank,
    id: coin.id,
    symbol: coin.symbol,
    name: coin.name,
    image: coin.image,
    current_price: currentPrice,
    price_change_percentage_1h: generatePercentageChange(5),
    price_change_percentage_24h: generatePercentageChange(20),
    price_change_percentage_7d: generatePercentageChange(30),
    market_cap: marketCap,
    total_volume: marketCap * (0.05 + Math.random() * 0.15),
    circulating_supply: circulatingSupply,
    max_supply: coin.id === 'bitcoin' ? 21000000 : 
               coin.id === 'ethereum' ? null :
               Math.random() > 0.5 ? circulatingSupply * (1.5 + Math.random()) : null,
    sparkline_in_7d: {
      price: generateSparkline(currentPrice)
    }
  };
}

export function generateMockCoinsData(page: number = 1, perPage: number = 100): {
  coins: CoinData[];
  totalCoins: number;
  totalPages: number;
  currentPage: number;
} {
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  
  const allCoins = TOP_1000_COINS.slice(0, 1000).map((coin, index) => 
    generateCoinData(coin, index + 1)
  );
  
  const paginatedCoins = allCoins.slice(startIndex, endIndex);
  
  return {
    coins: paginatedCoins,
    totalCoins: allCoins.length,
    totalPages: Math.ceil(allCoins.length / perPage),
    currentPage: page
  };
}

export function getGlobalMarketData() {
  const { coins } = generateMockCoinsData(1, 1000);
  
  const totalMarketCap = coins.reduce((sum, coin) => sum + coin.market_cap, 0);
  const total24hVolume = coins.reduce((sum, coin) => sum + coin.total_volume, 0);
  const btcMarketCap = coins.find(c => c.id === 'bitcoin')?.market_cap || 0;
  const ethMarketCap = coins.find(c => c.id === 'ethereum')?.market_cap || 0;
  
  return {
    total_market_cap: totalMarketCap,
    total_24h_volume: total24hVolume,
    btc_dominance: (btcMarketCap / totalMarketCap) * 100,
    eth_dominance: (ethMarketCap / totalMarketCap) * 100,
    total_cryptocurrencies: 1000,
    active_markets: 45231
  };
}

export function getTrendingCoins(): CoinData[] {
  const { coins } = generateMockCoinsData(1, 50);
  return coins
    .sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h)
    .slice(0, 5);
}

export function getTopGainers(): CoinData[] {
  const { coins } = generateMockCoinsData(1, 100);
  return coins
    .filter(coin => coin.price_change_percentage_24h > 0)
    .sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h)
    .slice(0, 5);
}

export function getTopLosers(): CoinData[] {
  const { coins } = generateMockCoinsData(1, 100);
  return coins
    .filter(coin => coin.price_change_percentage_24h < 0)
    .sort((a, b) => a.price_change_percentage_24h - b.price_change_percentage_24h)
    .slice(0, 5);
}

export function getCoinById(coinId: string): CoinData | null {
  const coinIndex = TOP_1000_COINS.findIndex(coin => coin.id === coinId);
  if (coinIndex === -1) return null;
  
  const coinData = generateCoinData(TOP_1000_COINS[coinIndex], coinIndex + 1);
  
  // Add more detailed sparkline data for coin detail page
  const extendedSparkline = [];
  for (let i = 0; i < 365; i++) {
    const randomVariation = (Math.random() - 0.5) * 0.1;
    const price = coinData.current_price * (1 + randomVariation);
    extendedSparkline.push(price);
  }
  
  return {
    ...coinData,
    sparkline_in_7d: {
      price: extendedSparkline
    }
  };
}